import { isEmpty } from 'lodash';
import { LocationChangedAction, replace } from 'redux-little-router';
import { combineEpics } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../actions';
import {
    getBannersForEdit,
    getBaseInfoForBannerCreation,
    ResetBannersEdit,
    resetBannersEdit
} from '../actions/bannersEdit';
import {
    getCampaignsForEdit,
    getRetargetingConditionsForCampaignsEdit
} from '../actions/campaignsEdit';
import { requestClientMobileAppsData } from '../actions/clientMobileAppsData';
import {
    DummyAction,
    dummyAction,
    fetchCommonData,
    hideRecommendationsTooltip,
    updateDocumentTitle,
    updateStringDicts
} from '../actions/common';
import { updateDealsSourceParams } from '../actions/deals';
import { fetchFreelancerCard } from '../actions/freelancers/freelancer-card';
import { fetchInitialFreelancerData } from '../actions/freelancers/freelancer-card-edit';
import { fetchCustomersData } from '../actions/freelancers/freelancer-customers';
import { fetchFreelancersListData } from '../actions/freelancers/freelancers-list';
import { fetchGoalsData } from '../actions/goals';
import { requestGridRow, updateCalcTypeFilter } from '../actions/grid-screen';
import {
    getCampaignBasicData,
    getGroupsForEditAction,
    getRetargetingConditionsForGroupsEdit
} from '../actions/groupsEdit';
import { getInternalBannersForEditOrCreation } from '../actions/internalBannersEdit';
import { fetchMarketerCardData } from '../actions/marketers/marketerCard';
import { fetchMarketers } from '../actions/marketers/marketersList';
import { requestMinusPhraseLibraryGridRow } from '../actions/minusPhraseLibrary';
import { fetchApp, fetchAppTrackersTemplates } from '../actions/mobile-app';
import { fetchProductsData } from '../actions/products';
import { fetchRecommendations } from '../actions/recommendations';
import {
    fetchRecommendationsGridSummary,
    fetchRecommendationsItems
} from '../actions/recommendations-grid';
import { fetchReports } from '../actions/reportsClient';
import { fetchReportsDomain } from '../actions/reportsDomain';
import { getProductsTitle } from '../components/Products/Products';
import { awaitCommonData, notOfRoute, ofNewVisit, ofRoute } from '../operators';
import { RoutesPath } from '../routes/index';
import { selectNormalizedDealsSourceParams } from '../selectors/deals';
import { selectFreelancersListSortParams } from '../selectors/freelancers';
import { selectRecommendationItemsSortParams } from '../selectors/recommendations';
import { castStringToEnum } from '../service/CastHelpers';
import {
    preparePeriodFilterAfterLocalStorage,
    serializePeriodFilter
} from '../service/FilterHelpers/FilterHelpers';
import { Session } from '../service/Session/Session';
import { UrlHelpers } from '../service/UrlHelpers';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { DnaEpic } from '../typings/epic';
import {
    GridScreenUrlSearchQueryParams,
    GridScreenUrlSearchRouteParams
} from '../typings/grid';
import { RecommendationType } from '../typings/recommendations';
import { RegionInputIds } from '../typings/regions';

const campaignsEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.campaignsEdit),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;

            return [
                getCampaignsForEdit(),
                getRetargetingConditionsForCampaignsEdit(login)
            ];
        })
    );
};

const groupsEditRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.groupsEdit),
        awaitCommonData(action$, store$),
        filter((action: LocationChangedAction) => {
            const { route } = action.payload;
            const { previous, query } = store$.value.router;

            return (
                (query !== undefined &&
                    query.forceGroupsEditNewVisit === '1') ||
                route !== (previous && previous.route)
            );
        }),
        mergeMap(
            (): Observable<Action> => {
                const {
                    commonData: { stringDicts, accessRights }
                } = store$.value;

                return from(
                    deps.groupsDataProvider.getUaTraits(
                        stringDicts,
                        accessRights && accessRights.canEditInternalAds
                    )
                ).pipe(
                    mergeMap(stringDictsResult => {
                        return [updateStringDicts(stringDictsResult)];
                    })
                );
            }
        ),
        switchMap(updateStringDictsAction => {
            const { login } = store$.value.commonData;
            const {
                editGroups: { campaignsIds, isNew }
            } = store$.value;
            const observables: Array<Action> = [fetchCommonData(login)];

            if (isNew) {
                observables.push(getCampaignBasicData(login, campaignsIds[0]));
                observables.push(updateStringDictsAction);
            } else {
                observables.push(getGroupsForEditAction(login));
                observables.push(getRetargetingConditionsForGroupsEdit(login));
            }

            return observables;
        })
    );
};

const bannersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.bannersEdit),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            const {
                editBanners: { campaignsIds, groupsIds, isNew }
            } = store$.value;
            const observables: Array<Action> = [fetchCommonData(login)];

            if (isNew) {
                observables.push(
                    getBaseInfoForBannerCreation(login, campaignsIds, groupsIds)
                );
            } else {
                observables.push(getBannersForEdit(login));
            }

            return observables;
        })
    );
};

const internalBannersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.internalBannersEdit),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return [
                fetchCommonData(login),
                getInternalBannersForEditOrCreation(login)
            ];
        })
    );
};

const freelancersListRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancersList),
        ofNewVisit(store$),
        switchMap((action: LocationChangedAction) => {
            const state = store$.value;
            const { login } = store$.value.commonData;
            const { query = {} } = action.payload;
            const sortParams = selectFreelancersListSortParams(state);
            const freelancerLogins = query.freelancerLogins
                ? query.freelancerLogins.split(',')
                : undefined;

            return of(
                fetchFreelancersListData(login, sortParams, freelancerLogins)
            );
        })
    );
};

const freelancerCardRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancerCard),
        ofNewVisit(store$),
        switchMap(
            (action: LocationChangedAction): Observable<Action> => {
                const { params = {} } = action.payload;
                const freelancerLogin = params.freelancerLogin;
                if (!freelancerLogin) {
                    return of(replace(UrlHelpers.getFreelancersListUrl()));
                }

                const { login } = store$.value.commonData;

                return of(fetchFreelancerCard(freelancerLogin, login));
            }
        )
    );
};

const freelancersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancersEdit),
        ofNewVisit(store$),
        switchMap(
            (action: LocationChangedAction): Observable<Action> => {
                const { params = {} } = action.payload;
                const freelancerLogin = params.freelancerLogin;
                if (!freelancerLogin) {
                    return of(replace(UrlHelpers.getFreelancersListUrl()));
                }

                return of(
                    fetchInitialFreelancerData(
                        freelancerLogin,
                        RegionInputIds.FreelancerRegionInputId
                    )
                );
            }
        )
    );
};

const freelancerCustomersRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancerCustomers),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return of(fetchCustomersData(login));
        })
    );
};

const recommendationsPageRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.recommendationsPage),
        ofNewVisit(store$),
        switchMap(() => [fetchRecommendations(), hideRecommendationsTooltip()])
    );
};

const recommendationsItemsRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.recommendationsItems),
        switchMap((action: LocationChangedAction) => {
            const state = store$.value;
            const {
                commonData: { login },
                router: { previous }
            } = state;
            const { params = {}, route } = action.payload;
            const recommendationType = castStringToEnum(
                params.type,
                RecommendationType
            );
            if (!recommendationType) {
                throw Error('Неизвестный тип рекомендации');
            }
            const sortParams = selectRecommendationItemsSortParams(state);

            if (previous && previous.route === route) {
                deps.segmentManager.reset();
                return of(
                    fetchRecommendationsGridSummary(recommendationType, login)
                );
            }

            return [
                fetchCommonData(login),
                fetchRecommendationsItems(
                    recommendationType,
                    login,
                    0,
                    sortParams
                ),
                fetchRecommendationsGridSummary(recommendationType, login)
            ];
        })
    );
};

export const gridRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.grid, RoutesPath.shortGrid),
        switchMap(
            (action: LocationChangedAction): Observable<Action> | Action[] => {
                const {
                    router: { pathname, previous, query = {}, params = {} },
                    grid: { filterDict = {} },
                    commonData: { login }
                } = store$.value;

                const additionalActions: Action[] = [];

                // убрать updateCalcTypeFilter в DIRECT-98318
                if (
                    previous &&
                    previous.route === RoutesPath.internalBannersEdit
                ) {
                    additionalActions.push(
                        updateCalcTypeFilter(CampaignsCalcTypeFilter.CPC)
                    );
                }

                const queryFilter =
                    query[GridScreenUrlSearchQueryParams.filter];
                const view = params[GridScreenUrlSearchRouteParams.view];
                const { gridFilter = {} } = Session.getValueFromCurrentSession(
                    login
                );
                const gridLocalStorageFilterValue = gridFilter[view];
                const statPreset =
                    query[GridScreenUrlSearchQueryParams.statPreset];
                const statFrom = query[GridScreenUrlSearchQueryParams.statFrom];
                const statTo = query[GridScreenUrlSearchQueryParams.statTo];
                const { statPeriod } = Session.getValueFromCurrentSession(
                    login
                );
                let resultQuery = {};

                if (
                    !queryFilter &&
                    filterDict[view] !== '' &&
                    gridLocalStorageFilterValue
                ) {
                    resultQuery = {
                        ...resultQuery,
                        filter: gridLocalStorageFilterValue
                    };
                }

                // Если периода нету в URL, но он есть в LocalStorage
                if (
                    statPeriod !== undefined &&
                    !statPreset &&
                    !statFrom &&
                    !statTo
                ) {
                    const serializedPeriod = serializePeriodFilter(
                        preparePeriodFilterAfterLocalStorage(statPeriod)
                    );
                    resultQuery = {
                        ...resultQuery,
                        ...serializedPeriod
                    };
                }

                if (!isEmpty(resultQuery)) {
                    return of(
                        replace(
                            {
                                pathname: pathname,
                                query: resultQuery
                            },
                            {
                                persistQuery: true
                            }
                        )
                    );
                }

                deps.segmentManager.reset();

                if (previous && previous.route === action.payload.route) {
                    return EMPTY;
                }

                // убрать additionalActions в DIRECT-98318
                return additionalActions.concat([
                    requestGridRow(0),
                    fetchGoalsData()
                ]);
            }
        )
    );
};

const clientReportsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.offlineReports),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return [fetchCommonData(login), fetchReports(login)];
        })
    );
};

const resetBannersEditEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        notOfRoute(RoutesPath.bannersEdit),
        ofNewVisit(store$),
        switchMap(
            (): Observable<ResetBannersEdit | DummyAction> => {
                const state = store$.value;
                const {
                    router: { previous }
                } = state;

                if (previous && previous.route === RoutesPath.bannersEdit) {
                    return of(resetBannersEdit());
                }

                return of(dummyAction);
            }
        )
    );
};

const domainReportsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.domainsReports),
        ofNewVisit(store$),
        switchMap(() => of(fetchReportsDomain()))
    );
};

const mobileAppsListRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsList),
        ofNewVisit(store$),
        switchMap(() => of(requestClientMobileAppsData()))
    );
};

const mobileAppsCreateRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsCreate),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return [
                requestClientMobileAppsData(),
                fetchAppTrackersTemplates(login)
            ];
        })
    );
};

const mobileAppsEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsEdit),
        ofNewVisit(store$),
        switchMap((action: LocationChangedAction) => {
            const { login } = store$.value.commonData;
            const { params = {} } = action.payload;
            const mobileAppId = params.mobileAppId || '0';

            return [
                requestClientMobileAppsData(),
                fetchApp(mobileAppId, login)
            ];
        })
    );
};

const dealsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.deals),
        switchMap(() => {
            const state = store$.value;
            const { login } = state.commonData;
            const dealSourceParams = selectNormalizedDealsSourceParams(state);

            return [
                fetchCommonData(login),
                updateDealsSourceParams(dealSourceParams)
            ];
        })
    );
};

const productsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.products),
        ofNewVisit(store$),
        switchMap(() => {
            return of(fetchProductsData());
        })
    );
};

const productsRouteTitleEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.products),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(() => {
            return of(
                updateDocumentTitle(
                    getProductsTitle(store$.value.commonData.accessRights)
                )
            );
        })
    );
};

const marketersRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.marketers),
        ofNewVisit(store$),
        switchMap(() => {
            return of(fetchProductsData(), fetchMarketers());
        })
    );
};

const marketerCardRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.marketerCard),
        ofNewVisit(store$),
        switchMap(
            (action: LocationChangedAction): Observable<Action> => {
                const { params = {} } = store$.value.router;

                return of(
                    fetchProductsData(),
                    fetchMarketerCardData(params.id)
                );
            }
        )
    );
};

const minusPhrasesLibraryEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.minusPhrasesLibrary),
        ofNewVisit(store$),
        switchMap(
            (): Observable<Action> => {
                deps.segmentManager.reset();

                return of(requestMinusPhraseLibraryGridRow(0));
            }
        )
    );
};

export const routerLocationChangedEpic = combineEpics(
    campaignsEditRouteEpic,
    groupsEditRouteEpic,
    bannersEditRouteEpic,
    internalBannersEditRouteEpic,
    freelancersListRouteEpic,
    freelancerCardRouteEpic,
    freelancersEditRouteEpic,
    freelancerCustomersRouteEpic,
    recommendationsPageRouteEpic,
    recommendationsItemsRouteEpic,
    gridRouteEpic,
    clientReportsRouteEpic,
    resetBannersEditEpic,
    domainReportsRouteEpic,
    mobileAppsListRouteEpic,
    mobileAppsCreateRouteEpic,
    mobileAppsEditRouteEpic,
    dealsRouteEpic,
    productsRouteEpic,
    marketersRouteEpic,
    marketerCardRouteEpic,
    minusPhrasesLibraryEpic,
    productsRouteTitleEpic
);
